// CoursesEnrollmentForm.js - Course enrollment form with multiple course selection
import { DEVICES } from '../../../constants/breakpoints';

export const createCoursesEnrollmentFormElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Class names
  const sectionClass = 'courses-enrollment-section';
  const containerClass = 'courses-enrollment-container';
  const headerClass = 'courses-enrollment-header';
  const titleClass = 'courses-enrollment-title';
  const subtitleClass = 'courses-enrollment-subtitle';
  const contentClass = 'courses-enrollment-content';
  const coursesGridClass = 'courses-enrollment-grid';
  const courseCardClass = 'courses-enrollment-card';
  const courseImageClass = 'courses-enrollment-image';
  const courseInfoClass = 'courses-enrollment-info';
  const courseTitleClass = 'courses-enrollment-course-title';
  const coursePriceClass = 'courses-enrollment-price';
  const courseCheckboxClass = 'courses-enrollment-checkbox';
  const formSectionClass = 'courses-enrollment-form-section';
  const formClass = 'courses-enrollment-form';
  const formGroupClass = 'courses-enrollment-form-group';
  const formLabelClass = 'courses-enrollment-form-label';
  const formInputClass = 'courses-enrollment-form-input';
  const formSelectClass = 'courses-enrollment-form-select';
  const formButtonClass = 'courses-enrollment-form-button';
  const summaryClass = 'courses-enrollment-summary';
  const successMessageClass = 'courses-enrollment-success';
  const errorMessageClass = 'courses-enrollment-error';

  // Desktop Styles
  const sectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '40px',
    paddingRight: '40px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.08) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.06) 0%, transparent 60%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)
    `,
    color: '#1e293b',
    overflow: 'hidden'
  };

  const containerStyles = {
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
    zIndex: '2'
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const titleStyles = {
    fontSize: '56px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-0.02em',
    lineHeight: '1.1'
  };

  const subtitleStyles = {
    fontSize: '20px',
    color: '#64748b',
    lineHeight: '1.6',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontWeight: '400',
    fontFamily: '"Inter", sans-serif'
  };

  const contentStyles = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '60px',
    alignItems: 'start'
  };

  const coursesGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '40px'
  };

  const courseCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '2px solid #e2e8f0',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative'
  };

  const courseImageStyles = {
    width: '100%',
    height: '200px',
    objectFit: 'cover'
  };

  const courseInfoStyles = {
    padding: '24px'
  };

  const courseTitleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '12px',
    fontFamily: '"Inter", sans-serif'
  };

  const coursePriceStyles = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#6366f1',
    fontFamily: '"Inter", sans-serif'
  };

  const courseCheckboxStyles = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '24px',
    height: '24px',
    accentColor: '#6366f1'
  };

  const formSectionStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    padding: '40px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    position: 'sticky',
    top: '120px'
  };

  const formStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const formGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const formLabelStyles = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: '"Inter", sans-serif'
  };

  const formInputStyles = {
    padding: '16px 20px',
    backgroundColor: '#f9fafb',
    border: '1px solid #d1d5db',
    borderRadius: '12px',
    color: '#1e293b',
    fontSize: '16px',
    fontFamily: '"Inter", sans-serif',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const formButtonStyles = {
    padding: '18px 40px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '20px'
  };

  const summaryStyles = {
    backgroundColor: '#f8fafc',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '24px'
  };

  const successMessageStyles = {
    padding: '15px',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '12px',
    marginTop: '20px',
    display: 'none',
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px'
  };

  const errorMessageStyles = {
    padding: '15px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '12px',
    marginTop: '20px',
    display: 'none',
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px'
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    newRegistry[DEVICES.DESKTOP].set(sectionClass, sectionStyles);
    newRegistry[DEVICES.DESKTOP].set(containerClass, containerStyles);
    newRegistry[DEVICES.DESKTOP].set(headerClass, headerStyles);
    newRegistry[DEVICES.DESKTOP].set(titleClass, titleStyles);
    newRegistry[DEVICES.DESKTOP].set(subtitleClass, subtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contentClass, contentStyles);
    newRegistry[DEVICES.DESKTOP].set(coursesGridClass, coursesGridStyles);
    newRegistry[DEVICES.DESKTOP].set(courseCardClass, courseCardStyles);
    newRegistry[DEVICES.DESKTOP].set(courseImageClass, courseImageStyles);
    newRegistry[DEVICES.DESKTOP].set(courseInfoClass, courseInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(courseTitleClass, courseTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(coursePriceClass, coursePriceStyles);
    newRegistry[DEVICES.DESKTOP].set(courseCheckboxClass, courseCheckboxStyles);
    newRegistry[DEVICES.DESKTOP].set(formSectionClass, formSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(formClass, formStyles);
    newRegistry[DEVICES.DESKTOP].set(formGroupClass, formGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(formLabelClass, formLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(formInputClass, formInputStyles);
    newRegistry[DEVICES.DESKTOP].set(formSelectClass, formInputStyles);
    newRegistry[DEVICES.DESKTOP].set(formButtonClass, formButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(summaryClass, summaryStyles);
    newRegistry[DEVICES.DESKTOP].set(successMessageClass, successMessageStyles);
    newRegistry[DEVICES.DESKTOP].set(errorMessageClass, errorMessageStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contentClass, { gridTemplateColumns: '1fr', gap: '40px' });
    newRegistry[DEVICES.TABLET].set(titleClass, { fontSize: '48px' });
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(sectionClass, { paddingTop: '80px', paddingBottom: '80px', paddingLeft: '20px', paddingRight: '20px' });
    newRegistry[DEVICES.MOBILE].set(titleClass, { fontSize: '36px' });
    newRegistry[DEVICES.MOBILE].set(formSectionClass, { padding: '24px' });
    
    return newRegistry;
  });

  // Add classes to global registry
  setGlobalClassNames(prev => new Set([
    ...prev,
    sectionClass, containerClass, headerClass, titleClass, subtitleClass,
    contentClass, coursesGridClass, courseCardClass, courseImageClass,
    courseInfoClass, courseTitleClass, coursePriceClass, courseCheckboxClass,
    formSectionClass, formClass, formGroupClass, formLabelClass,
    formInputClass, formSelectClass, formButtonClass, summaryClass,
    successMessageClass, errorMessageClass
  ]));

  // Generate IDs
  const baseId = Date.now();
  const sectionId = baseId;
  const containerId = baseId + 1;
  const headerId = baseId + 2;
  const titleId = baseId + 3;
  const subtitleId = baseId + 4;
  const contentId = baseId + 5;
  const coursesGridId = baseId + 6;
  const formSectionId = baseId + 7;

  // Course card IDs
  const course1Id = baseId + 10;
  const course1CheckboxId = baseId + 11;
  const course1ImageId = baseId + 12;
  const course1InfoId = baseId + 13;
  const course1TitleId = baseId + 14;

  const course2Id = baseId + 16;
  const course2CheckboxId = baseId + 17;
  const course2ImageId = baseId + 18;
  const course2InfoId = baseId + 19;
  const course2TitleId = baseId + 20;

  const course3Id = baseId + 22;
  const course3CheckboxId = baseId + 23;
  const course3ImageId = baseId + 24;
  const course3InfoId = baseId + 25;
  const course3TitleId = baseId + 26;

  const course4Id = baseId + 28;
  const course4CheckboxId = baseId + 29;
  const course4ImageId = baseId + 30;
  const course4InfoId = baseId + 31;
  const course4TitleId = baseId + 32;

  const course5Id = baseId + 34;
  const course5CheckboxId = baseId + 35;
  const course5ImageId = baseId + 36;
  const course5InfoId = baseId + 37;
  const course5TitleId = baseId + 38;

  const course6Id = baseId + 67;
  const course6CheckboxId = baseId + 68;
  const course6ImageId = baseId + 69;
  const course6InfoId = baseId + 70;
  const course6TitleId = baseId + 71;

  const course7Id = baseId + 73;
  const course7CheckboxId = baseId + 74;
  const course7ImageId = baseId + 75;
  const course7InfoId = baseId + 76;
  const course7TitleId = baseId + 77;

  // Form field IDs
  const formId = baseId + 40;
  const summaryId = baseId + 41;
  const summaryTitleId = baseId + 42;
  const selectedCoursesId = baseId + 43;
  const nameGroupId = baseId + 47;
  const nameLabelId = baseId + 48;
  const nameInputId = baseId + 49;
  const emailGroupId = baseId + 50;
  const emailLabelId = baseId + 51;
  const emailInputId = baseId + 52;
  const phoneGroupId = baseId + 53;
  const phoneLabelId = baseId + 54;
  const phoneInputId = baseId + 55;
  const experienceGroupId = baseId + 56;
  const experienceLabelId = baseId + 57;
  const experienceSelectId = baseId + 58;
  const goalGroupId = baseId + 59;
  const goalLabelId = baseId + 60;
  const goalTextareaId = baseId + 61;
  const hiddenCoursesId = baseId + 62;
  const submitButtonId = baseId + 64;
  const successMessageId = baseId + 65;
  const errorMessageId = baseId + 66;

  const elements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Courses Enrollment Section',
      parentId: null,
      className: sectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: { 'data-element': 'courses-enrollment-section' }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: containerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: { 'data-element': 'enrollment-container' }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: headerClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: { 'data-element': 'enrollment-header' }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Enrollment Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: titleClass,
      styles: {},
      children: [],
      text: 'Enroll in Your Courses',
      attributes: { 'data-element': 'enrollment-title' }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Enrollment Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: subtitleClass,
      styles: {},
      children: [],
      text: 'Select your courses and complete your enrollment to start your learning journey today.',
      attributes: { 'data-element': 'enrollment-subtitle' }
    },

    // Content Grid
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contentClass,
      styles: {},
      children: [coursesGridId, formSectionId],
      text: '',
      attributes: { 'data-element': 'enrollment-content' }
    },

    // Courses Grid Container
    {
      id: coursesGridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Grid',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: coursesGridClass,
      styles: {},
      children: [course1Id, course2Id, course3Id, course4Id, course5Id, course6Id, course7Id],
      text: '',
      attributes: { 'data-element': 'courses-grid' }
    },

    // Course 1 - Computer Operator
    {
      id: course1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Computer Operator Course',
      parentId: coursesGridId,
      hierarchyName: getHierarchyName('div'),
      className: courseCardClass,
      styles: {},
      children: [course1CheckboxId, course1ImageId, course1InfoId],
      text: '',
      attributes: { 'data-element': 'course-card', 'data-course': 'computer-operator' }
    },

    // Course 1 Checkbox
    {
      id: course1CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Computer Operator Checkbox',
      parentId: course1Id,
      hierarchyName: getHierarchyName('input'),
      className: courseCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'courses',
        'value': 'computer-operator',
        'data-element': 'course-checkbox'
      }
    },

    // Course 1 Image
    {
      id: course1ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Computer Operator Image',
      parentId: course1Id,
      hierarchyName: getHierarchyName('image'),
      className: courseImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Computer Operator',
        'data-element': 'course-image'
      }
    },

    // Course 1 Info
    {
      id: course1InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Computer Operator Info',
      parentId: course1Id,
      hierarchyName: getHierarchyName('div'),
      className: courseInfoClass,
      styles: {},
      children: [course1TitleId],
      text: '',
      attributes: { 'data-element': 'course-info' }
    },

    // Course 1 Title
    {
      id: course1TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Computer Operator Title',
      parentId: course1InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: courseTitleClass,
      styles: {},
      children: [],
      text: 'Computer Operator',
      attributes: { 'data-element': 'course-title' }
    },

    // Course 2 - Textile Designing
    {
      id: course2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Textile Designing Course',
      parentId: coursesGridId,
      hierarchyName: getHierarchyName('div'),
      className: courseCardClass,
      styles: {},
      children: [course2CheckboxId, course2ImageId, course2InfoId],
      text: '',
      attributes: { 'data-element': 'course-card', 'data-course': 'textile-designing' }
    },

    // Course 2 Checkbox
    {
      id: course2CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Textile Designing Checkbox',
      parentId: course2Id,
      hierarchyName: getHierarchyName('input'),
      className: courseCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'courses',
        'value': 'textile-designing',
        'data-element': 'course-checkbox'
      }
    },

    // Course 2 Image
    {
      id: course2ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Textile Designing Image',
      parentId: course2Id,
      hierarchyName: getHierarchyName('image'),
      className: courseImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Textile Designing',
        'data-element': 'course-image'
      }
    },

    // Course 2 Info
    {
      id: course2InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Textile Designing Info',
      parentId: course2Id,
      hierarchyName: getHierarchyName('div'),
      className: courseInfoClass,
      styles: {},
      children: [course2TitleId],
      text: '',
      attributes: { 'data-element': 'course-info' }
    },

    // Course 2 Title
    {
      id: course2TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Textile Designing Title',
      parentId: course2InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: courseTitleClass,
      styles: {},
      children: [],
      text: 'Textile Designing',
      attributes: { 'data-element': 'course-title' }
    },

    // Course 3 - Import Export
    {
      id: course3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Import Export Course',
      parentId: coursesGridId,
      hierarchyName: getHierarchyName('div'),
      className: courseCardClass,
      styles: {},
      children: [course3CheckboxId, course3ImageId, course3InfoId],
      text: '',
      attributes: { 'data-element': 'course-card', 'data-course': 'import-export' }
    },

    // Course 3 Checkbox
    {
      id: course3CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Import Export Checkbox',
      parentId: course3Id,
      hierarchyName: getHierarchyName('input'),
      className: courseCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'courses',
        'value': 'import-export',
        'data-element': 'course-checkbox'
      }
    },

    // Course 3 Image
    {
      id: course3ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Import Export Image',
      parentId: course3Id,
      hierarchyName: getHierarchyName('image'),
      className: courseImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Import Export',
        'data-element': 'course-image'
      }
    },

    // Course 3 Info
    {
      id: course3InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Import Export Info',
      parentId: course3Id,
      hierarchyName: getHierarchyName('div'),
      className: courseInfoClass,
      styles: {},
      children: [course3TitleId],
      text: '',
      attributes: { 'data-element': 'course-info' }
    },

    // Course 3 Title
    {
      id: course3TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Import Export Title',
      parentId: course3InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: courseTitleClass,
      styles: {},
      children: [],
      text: 'Import Export Procedure',
      attributes: { 'data-element': 'course-title' }
    },

    // Course 4 - Digital Marketing
    {
      id: course4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Digital Marketing Course',
      parentId: coursesGridId,
      hierarchyName: getHierarchyName('div'),
      className: courseCardClass,
      styles: {},
      children: [course4CheckboxId, course4ImageId, course4InfoId],
      text: '',
      attributes: { 'data-element': 'course-card', 'data-course': 'digital-marketing' }
    },

    // Course 4 Checkbox
    {
      id: course4CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Digital Marketing Checkbox',
      parentId: course4Id,
      hierarchyName: getHierarchyName('input'),
      className: courseCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'courses',
        'value': 'digital-marketing',
        'data-element': 'course-checkbox'
      }
    },

    // Course 4 Image
    {
      id: course4ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Digital Marketing Image',
      parentId: course4Id,
      hierarchyName: getHierarchyName('image'),
      className: courseImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Digital Marketing',
        'data-element': 'course-image'
      }
    },

    // Course 4 Info
    {
      id: course4InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Digital Marketing Info',
      parentId: course4Id,
      hierarchyName: getHierarchyName('div'),
      className: courseInfoClass,
      styles: {},
      children: [course4TitleId],
      text: '',
      attributes: { 'data-element': 'course-info' }
    },

    // Course 4 Title
    {
      id: course4TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Digital Marketing Title',
      parentId: course4InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: courseTitleClass,
      styles: {},
      children: [],
      text: 'Digital Marketing',
      attributes: { 'data-element': 'course-title' }
    },

    // Course 5 - Dress Making
    {
      id: course5Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Dress Making Course',
      parentId: coursesGridId,
      hierarchyName: getHierarchyName('div'),
      className: courseCardClass,
      styles: {},
      children: [course5CheckboxId, course5ImageId, course5InfoId],
      text: '',
      attributes: { 'data-element': 'course-card', 'data-course': 'dress-making' }
    },

    // Course 5 Checkbox
    {
      id: course5CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Dress Making Checkbox',
      parentId: course5Id,
      hierarchyName: getHierarchyName('input'),
      className: courseCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'courses',
        'value': 'dress-making',
        'data-element': 'course-checkbox'
      }
    },

    // Course 5 Image
    {
      id: course5ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Dress Making Image',
      parentId: course5Id,
      hierarchyName: getHierarchyName('image'),
      className: courseImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Dress Making',
        'data-element': 'course-image'
      }
    },

    // Course 5 Info
    {
      id: course5InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Dress Making Info',
      parentId: course5Id,
      hierarchyName: getHierarchyName('div'),
      className: courseInfoClass,
      styles: {},
      children: [course5TitleId],
      text: '',
      attributes: { 'data-element': 'course-info' }
    },

    // Course 5 Title
    {
      id: course5TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Dress Making Title',
      parentId: course5InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: courseTitleClass,
      styles: {},
      children: [],
      text: 'Dress Making',
      attributes: { 'data-element': 'course-title' }
    },

    // Course 6 - Beautician
    {
      id: course6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Beautician Course',
      parentId: coursesGridId,
      hierarchyName: getHierarchyName('div'),
      className: courseCardClass,
      styles: {},
      children: [course6CheckboxId, course6ImageId, course6InfoId],
      text: '',
      attributes: { 'data-element': 'course-card', 'data-course': 'beautician' }
    },

    // Course 6 Checkbox
    {
      id: course6CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Beautician Checkbox',
      parentId: course6Id,
      hierarchyName: getHierarchyName('input'),
      className: courseCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'courses',
        'value': 'beautician',
        'data-element': 'course-checkbox'
      }
    },

    // Course 6 Image
    {
      id: course6ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Beautician Image',
      parentId: course6Id,
      hierarchyName: getHierarchyName('image'),
      className: courseImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Beautician',
        'data-element': 'course-image'
      }
    },

    // Course 6 Info
    {
      id: course6InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Beautician Info',
      parentId: course6Id,
      hierarchyName: getHierarchyName('div'),
      className: courseInfoClass,
      styles: {},
      children: [course6TitleId],
      text: '',
      attributes: { 'data-element': 'course-info' }
    },

    // Course 6 Title
    {
      id: course6TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Beautician Title',
      parentId: course6InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: courseTitleClass,
      styles: {},
      children: [],
      text: 'Beautician',
      attributes: { 'data-element': 'course-title' }
    },

    // Course 7 - Electrician
    {
      id: course7Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Electrician Course',
      parentId: coursesGridId,
      hierarchyName: getHierarchyName('div'),
      className: courseCardClass,
      styles: {},
      children: [course7CheckboxId, course7ImageId, course7InfoId],
      text: '',
      attributes: { 'data-element': 'course-card', 'data-course': 'electrician' }
    },

    // Course 7 Checkbox
    {
      id: course7CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Electrician Checkbox',
      parentId: course7Id,
      hierarchyName: getHierarchyName('input'),
      className: courseCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'courses',
        'value': 'electrician',
        'data-element': 'course-checkbox'
      }
    },

    // Course 7 Image
    {
      id: course7ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Electrician Image',
      parentId: course7Id,
      hierarchyName: getHierarchyName('image'),
      className: courseImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Electrician',
        'data-element': 'course-image'
      }
    },

    // Course 7 Info
    {
      id: course7InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Electrician Info',
      parentId: course7Id,
      hierarchyName: getHierarchyName('div'),
      className: courseInfoClass,
      styles: {},
      children: [course7TitleId],
      text: '',
      attributes: { 'data-element': 'course-info' }
    },

    // Course 7 Title
    {
      id: course7TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Electrician Title',
      parentId: course7InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: courseTitleClass,
      styles: {},
      children: [],
      text: 'Electrician',
      attributes: { 'data-element': 'course-title' }
    },

    // Form Section
    {
      id: formSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Form Section',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: formSectionClass,
      styles: {},
      children: [summaryId, formId, successMessageId, errorMessageId],
      text: '',
      attributes: { 'data-element': 'enrollment-form-section' }
    },

    // Summary
    {
      id: summaryId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Enrollment Summary',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('div'),
      className: summaryClass,
      styles: {},
      children: [summaryTitleId, selectedCoursesId],
      text: '',
      attributes: { 'data-element': 'enrollment-summary' }
    },

    // Summary Title
    {
      id: summaryTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Summary Title',
      parentId: summaryId,
      hierarchyName: getHierarchyName('h3'),
      styles: { margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b' },
      children: [],
      text: 'Selected Courses',
      attributes: { 'data-element': 'summary-title' }
    },

    // Selected Courses Display
    {
      id: selectedCoursesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Selected Courses',
      parentId: summaryId,
      hierarchyName: getHierarchyName('div'),
      styles: { marginBottom: '16px', color: '#64748b' },
      children: [],
      text: 'No courses selected',
      attributes: { 'id': 'selected-courses', 'data-element': 'selected-courses' }
    },

    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Enrollment Form',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('form'),
      className: formClass,
      styles: {},
      children: [nameGroupId, emailGroupId, phoneGroupId, experienceGroupId, goalGroupId, hiddenCoursesId, submitButtonId],
      text: '',
      attributes: { 'data-element': 'enrollment-form', 'method': 'post' }
    },

    // Name Field
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Full Name *',
      attributes: { 'for': 'student-name', 'data-element': 'form-label' }
    },

    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'text',
        'id': 'student-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': true,
        'data-element': 'form-input'
      }
    },

    // Email Field
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Email Address *',
      attributes: { 'for': 'student-email', 'data-element': 'form-label' }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'email',
        'id': 'student-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': true,
        'data-element': 'form-input'
      }
    },

    // Phone Field
    {
      id: phoneGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [phoneLabelId, phoneInputId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Phone Label
    {
      id: phoneLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Phone Label',
      parentId: phoneGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Phone Number *',
      attributes: { 'for': 'student-phone', 'data-element': 'form-label' }
    },

    // Phone Input
    {
      id: phoneInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Phone Input',
      parentId: phoneGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'tel',
        'id': 'student-phone',
        'name': 'phone',
        'placeholder': 'Enter your phone number',
        'required': true,
        'data-element': 'form-input'
      }
    },

    // Experience Field
    {
      id: experienceGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Experience Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [experienceLabelId, experienceSelectId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Experience Label
    {
      id: experienceLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Experience Label',
      parentId: experienceGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Experience Level',
      attributes: { 'for': 'experience-level', 'data-element': 'form-label' }
    },

    // Experience Select
    {
      id: experienceSelectId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'select',
      name: 'Experience Select',
      parentId: experienceGroupId,
      hierarchyName: getHierarchyName('select'),
      className: formSelectClass,
      styles: {},
      children: [],
      text: `
        <option value="">Select your experience level</option>
        <option value="beginner">Beginner</option>
        <option value="intermediate">Intermediate</option>
        <option value="advanced">Advanced</option>
      `,
      attributes: { 
        'id': 'experience-level',
        'name': 'experience',
        'data-element': 'form-select'
      }
    },

    // Learning Goal Field
    {
      id: goalGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Goal Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [goalLabelId, goalTextareaId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Goal Label
    {
      id: goalLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Goal Label',
      parentId: goalGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Learning Goal',
      attributes: { 'for': 'learning-goal', 'data-element': 'form-label' }
    },

    // Goal Textarea
    {
      id: goalTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Goal Textarea',
      parentId: goalGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: formInputClass,
      styles: { minHeight: '80px', resize: 'vertical' },
      children: [],
      text: '',
      attributes: { 
        'id': 'learning-goal',
        'name': 'goal',
        'placeholder': 'Tell us about your learning goals...',
        'rows': '3',
        'data-element': 'form-textarea'
      }
    },

    // Hidden field for selected courses
    {
      id: hiddenCoursesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Hidden Courses Field',
      parentId: formId,
      hierarchyName: getHierarchyName('input'),
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'hidden',
        'id': 'selected-courses-data',
        'name': 'selectedCourses',
        'value': '',
        'data-element': 'hidden-courses'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: formButtonClass,
      styles: {},
      children: [],
      text: 'Complete Enrollment',
      attributes: { 'data-element': 'form-button', 'type': 'submit' },
    },

    // Success Message
    {
      id: successMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Success Message',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('div'),
      className: successMessageClass,
      styles: {},
      children: [],
      text: 'Enrollment submitted successfully! You will receive a confirmation email shortly.',
      attributes: { 'data-element': 'success-message', 'id': 'enrollment-success' }
    },

    // Error Message
    {
      id: errorMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Error Message',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('div'),
      className: errorMessageClass,
      styles: {},
      children: [],
      text: 'There was an error submitting your enrollment. Please try again.',
      attributes: { 'data-element': 'error-message', 'id': 'enrollment-error' }
    }
  ];

  // Add course selection functionality - simplified without total price
  const courseSelectionScript = `
    function initCourseSelection() {
      console.log('Course selection script loaded');
      
      const courseCards = document.querySelectorAll('[data-course]');
      
      // Try multiple ways to find the summary elements
      let selectedCoursesDiv = document.getElementById('selected-courses');
      
      // Fallback: try finding by data attributes
      if (!selectedCoursesDiv) {
        selectedCoursesDiv = document.querySelector('[data-element="selected-courses"]');
      }
      
      console.log('Found course cards:', courseCards.length);
      console.log('Selected courses div:', selectedCoursesDiv);
      
      function updateSummary() {
        console.log('updateSummary called');
        
        const selectedCheckboxes = document.querySelectorAll('input[name="courses"]:checked');
        console.log('Selected checkboxes:', selectedCheckboxes.length);
        
        let courseNames = [];
        
        selectedCheckboxes.forEach(checkbox => {
          console.log('Processing checkbox:', checkbox.value);
          courseNames.push(checkbox.value.replace('-', ' ').replace(/\\b\\w/g, l => l.toUpperCase()));
        });
        
        console.log('Course names:', courseNames);
        
        // Find elements again each time
        let currentSelectedCoursesDiv = document.getElementById('selected-courses') || document.querySelector('[data-element="selected-courses"]');
        
        console.log('Current selected courses div:', currentSelectedCoursesDiv);
        
        if (currentSelectedCoursesDiv) {
          if (courseNames.length === 0) {
            currentSelectedCoursesDiv.textContent = 'No courses selected';
          } else {
            // Clear existing content
            currentSelectedCoursesDiv.textContent = '';
            
            // Add each course as a separate text node
            courseNames.forEach((name, index) => {
              const courseItem = document.createElement('div');
              courseItem.style.marginBottom = '4px';
              courseItem.textContent = '• ' + name;
              currentSelectedCoursesDiv.appendChild(courseItem);
            });
          }
        } else {
          console.error('selected-courses element not found!');
        }
        
        // Update hidden form field
        const hiddenCoursesField = document.getElementById('selected-courses-data');
        
        if (hiddenCoursesField) {
          // Store selected courses as JSON string
          const selectedCoursesData = selectedCheckboxes.length > 0 ? 
            Array.from(selectedCheckboxes).map(cb => ({
              name: cb.value,
              title: cb.value.replace('-', ' ').replace(/\\b\\w/g, l => l.toUpperCase())
            })) : [];
          
          hiddenCoursesField.value = JSON.stringify(selectedCoursesData);
          console.log('Updated hidden courses field:', hiddenCoursesField.value);
        } else {
          console.error('Hidden courses field not found!');
        }
      }
      
      courseCards.forEach(card => {
        const checkbox = card.querySelector('input[type="checkbox"]');
        console.log('Processing card:', card, 'checkbox:', checkbox);
        
        if (checkbox) {
          card.addEventListener('click', function(e) {
            console.log('Card clicked:', e.target.type);
            if (e.target.type !== 'checkbox') {
              checkbox.checked = !checkbox.checked;
              console.log('Checkbox toggled to:', checkbox.checked);
              updateSummary();
            }
          });
          
          checkbox.addEventListener('change', function() {
            console.log('Checkbox changed:', this.checked);
            updateSummary();
            if (this.checked) {
              card.style.borderColor = '#6366f1';
              card.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
            } else {
              card.style.borderColor = '#e2e8f0';
              card.style.boxShadow = 'none';
            }
          });
        }
      });
      
      // Initial update
      console.log('Running initial update');
      updateSummary();
    }
    
    // Try multiple initialization strategies
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initCourseSelection);
    } else {
      // DOM is already loaded, try immediately
      initCourseSelection();
    }
    
    // Also try with a small delay as fallback
    setTimeout(initCourseSelection, 100);
    setTimeout(initCourseSelection, 500);
  `;

  return {
    enrollmentSection: elements[0],
    elements: elements
  };
}; 